import { Edit, Mail, Printer, Upload,Trash2 } from "lucide-react";
import moment from "moment";
import { TooltipText } from "../ui/tooltip";
import { SearchBar } from "../SearchBox/SearchBar";

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "PENDING":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "COMPLETED":
      return "bg-green-100 text-green-800 border-green-300";
    case "IN TRANSIT":
      return "bg-blue-100 text-blue-800 border-blue-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

export const getPurchaseColumns = ({
  selectedOrders,
  purchaseList,
  handleSelectAll,
  handleSelectOrder,
  onEditOrder,
  setSelectedOrderId,
}) => {
  const actions = (row) => [
    {
      key: "upload",
      icon: Upload,
      title: "Upload",
      className:"bg-green-100 text-green-700 hover:bg-green-200",
      onClick: () => setSelectedOrderId(row),
    },
    {
      key: "email",
      icon: Mail,
      title: "Email",
       className:"bg-purple-100 text-purple-700 hover:bg-purple-200",
      onClick: () => {},
    },
    {
      key: "print",
      icon: Printer,
      title: "Print",
       className:"bg-blue-100 text-blue-700 hover:bg-blue-200",
      onClick: () => {},
    },
    {
      key: "edit",
      icon: Edit,
      title: "Edit",
       className:"bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
      onClick: () => onEditOrder(row?.purchaseOrderId),
    },
  ];

  return [
    // ✅ Select
    {
      key: "select",
      label: () => (
        <input
          type="checkbox"
          onChange={handleSelectAll}
          checked={selectedOrders.length === purchaseList?.length}
          className="rounded border-slate-300 text-[#0072FF]"
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedOrders.includes(row?.rowNum)}
          onChange={() => handleSelectOrder(row?.rowNum)}
          className="rounded border-slate-300 text-[#0072FF]"
        />
      ),
    },

    // ✅ Order #
    {
      key: "poNumber",
      label: "Order #",
      render: (row) => (
        <button
          onClick={() => onEditOrder(row?.purchaseOrderId)}
          className="text-sm font-semibold text-[#0072FF] hover:underline"
        >
          {row?.poNumber || "-"}
        </button>
      ),
    },

    // ✅ PO Date
    {
      key: "poDate",
      label: "PO Date",
      render: (row) =>
       <span className="whitespace-nowrap">{ row?.poDate ? moment(row.poDate).format("DD MMM YYYY") : "-"}</span>,
    },

    // ✅ Vendor
    {
      key: "vendor",
      label: "Vendor",
      render: (row) => (
        <TooltipText content={row?.vendor}>
          <span className="block max-w-[180px] truncate">
            {row?.vendor || "-"}
          </span>
        </TooltipText>
      ),
    },

    // ✅ Ship To
    {
      key: "destination",
      label: "Ship To",
      render: (row) => (
        <TooltipText content={row?.destination}>
          <span className="block max-w-[150px] truncate">
            {row?.destination || "-"}
          </span>
        </TooltipText>
      ),
    },

    // ✅ Consignee
    {
      key: "consignee",
      label: "Consignee",
      render: (row) => (
        <TooltipText content={row?.consignee}>
          <span className="block max-w-[150px] truncate">
            {row?.consignee || "-"}
          </span>
        </TooltipText>
      ),
    },

    // ✅ Supplier
    {
      key: "supplier",
      label: "Supplier",
      render: (row) => (
        <TooltipText content={row?.supplier}>
          <span className="block max-w-[150px] truncate">
            {row?.supplier || "-"}
          </span>
        </TooltipText>
      ),
    },

    // ✅ SKU
    {
      key: "allSku",
      label: "SKU Code",
      render: (row) => (
        <TooltipText content={row?.allSku}>
          <span className="block font-mono max-w-[200px] truncate">
            {row?.allSku || "-"}
          </span>
        </TooltipText>
      ),
    },

    // ✅ PO Status
    {
      key: "poStatus",
      label: "PO Status",
      render: (row) => (
        <span
          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
            row?.poStatus
          )}`}
        >
          {row?.poStatus || "-"}
        </span>
      ),
    },

    // ✅ Actions
    {
      key: "actions",
      label: () => <span className="text-center block w-full">Actions</span>,
      render: (row) => (
        <div className="flex items-center justify-center space-x-2">
          {actions(row).map((action) => {
            const Icon = action.icon;

            return (
              <button
                key={action.key}
                onClick={action.onClick}
                title={action.title}
                className={`w-6 h-6 rounded flex items-center justify-center transition-all ${action.className || ""}`}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      ),
    },
  ];
};



export const getItemColumns = ({
  handleItemChange,
  uomTypeList,
  quantityTypeList,
}) => {
  return [
    {
      key: "sno",
      label: "SNO",
      render: (_, index) => (
        <span className="text-center block">{index + 1}</span>
      ),
    },

    {
      key: "sku",
      label: "SKU CODE",
      thClass: "px-2 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 min-w-[200px]",
      render: (row, index) => (
        <SearchBar
          searchAPI="v1/AutoSuggestion/GetSkuSuggestion?Prefix="
          value={row?.sku}
          selectReacord={(d) => {
            handleItemChange(index, "sku", d?.[0]);
            handleItemChange(index, "skuItemDesc", d?.[1]);
          }}
          inputStyle="!py-1"
          minword={1}
          showResults={0}
        />
      ),
    },

    {
      key: "desc",
      label: "ITEM DESC",
      render: (row, index) => (
        <input
          value={row?.skuItemDesc}
          onChange={(e) =>
            handleItemChange(index, "skuItemDesc", e.target.value)
          }
          className="w-32 px-2 py-1 text-xs border rounded dark:bg-slate-800"
        />
      ),
    },

    {
      key: "uot",
      label: "UOT",
      render: (row, index) => (
        <input
          type="number"
          value={row?.uot}
          onChange={(e) => {
            const val = e.target.value;
            handleItemChange(index, "uot", val);

            handleItemChange(
              index,
              "remainingUnit",
              val
            );

            handleItemChange(
              index,
              "caseCount",
              parseFloat(val || 0) *
                parseFloat(row?.casesPerUot || 0)
            );
          }}
          className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "casesPerUot",
      label: "CASES PER UOT",
      render: (row, index) => (
        <input
          type="number"
          value={row?.casesPerUot}
          onChange={(e) => {
            const val = e.target.value;

            handleItemChange(index, "casesPerUot", val);

            handleItemChange(
              index,
              "remainingCases",
              val
            );

            handleItemChange(
              index,
              "caseCount",
              parseFloat(row?.remainingUnit || 0) *
                parseFloat(val || 0)
            );
          }}
          className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "remainingUnit",
      label: "REMAINING UNIT",
      render: (row) => (
        <input
          readOnly
          value={row?.remainingUnit}
          className="w-20 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
        />
      ),
    },

    {
      key: "remainingCases",
      label: "REMAINING CASES",
      render: (row) => (
        <input
          readOnly
          value={row?.remainingCases}
          className="w-20 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
        />
      ),
    },

    {
      key: "caseCount",
      label: "CASE COUNT",
      render: (row, index) => (
        <div className="flex gap-1">
          <input
            readOnly
            value={
              parseFloat(row?.uot || 0) *
              parseFloat(row?.casesPerUot || 0)
            }
            className="w-16 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
          />

          <select
            value={row?.uomTypeId}
            onChange={(e) => {
              handleItemChange(index, "uomTypeId", e.target.value);
              handleItemChange(
                index,
                "uomType",
                e.target.options[e.target.selectedIndex].text
              );
            }}
            className="w-24 px-1 py-1 border rounded text-xs dark:bg-slate-800"
          >
            <option>SELECT</option>
            {uomTypeList?.map((uom, i) => (
              <option key={i} value={uom?.id}>
                {uom?.id}
              </option>
            ))}
          </select>
        </div>
      ),
    },

    {
      key: "packing",
      label: "PACKING",
      render: (row, index) => (
        <input
          type="number"
          value={row?.packing}
          onChange={(e) =>
            handleItemChange(index, "packing", e.target.value)
          }
          className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "quantity",
      label: "QUANTITY",
      render: (row, index) => (
        <div className="flex gap-1">
          <input
            type="number"
            value={row?.quantity}
            onChange={(e) =>
              handleItemChange(index, "quantity", e.target.value)
            }
            className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
          />

          <select
            value={row?.metricId}
            onChange={(e) => {
              handleItemChange(index, "metricId", e.target.value);
              handleItemChange(
                index,
                "metricType",
                e.target.options[e.target.selectedIndex].text
              );
            }}
            className="w-16 px-1 py-1 border rounded text-xs dark:bg-slate-800"
          >
            <option>SELECT</option>
            {quantityTypeList?.map((qty, i) => (
              <option key={i} value={qty?.id}>
                {qty?.incoterm}
              </option>
            ))}
          </select>
        </div>
      ),
    },

    {
      key: "price",
      label: "PRICE",
      render: (row, index) => (
        <input
          type="number"
          value={row?.price}
          onChange={(e) => {
            const val = e.target.value;
            handleItemChange(index, "price", val);

            handleItemChange(
              index,
              "total",
              (parseFloat(val || 0) *
                parseFloat(row?.quantity || 0)).toFixed(2)
            );
          }}
          className="w-20 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "total",
      label: "TOTAL",
      render: (row) => (
        <input
          readOnly
          value={row?.total}
          className="w-20 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
        />
      ),
    },

    {
      key: "warehouse",
      label: "WAREHOUSE",
      render: (row, index) => (
        <input
          value={row?.warehouse}
          onChange={(e) =>
            handleItemChange(index, "warehouse", e.target.value)
          }
          className="w-24 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "notes",
      label: "NOTES",
      render: (row, index) => (
        <input
          value={row?.notes}
          onChange={(e) =>
            handleItemChange(index, "notes", e.target.value)
          }
          className="w-20 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "delete",
      label: "DEL",
      render: (_, index) => (
        <button
          onClick={() =>
            handleItemChange(index, "isActive", false)
          }
          className="w-6 h-6 rounded flex items-center justify-center transition-all bg-red-100 text-red-700 hover:bg-red-200"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];
};